import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const getAuthHeaders = () => ({
  authorization: localStorage.getItem('access_token') || '',
});

const initialState = {
  invites: [],
  invitesPagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
  listLoading: false,
  actionLoading: false,
  error: null,
};

export const fetchCuratorInvites = createAsyncThunk(
  'curatorInvites/fetch',
  async ({ page = 1, limit = 20, search = '' } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/admin/curator-invites`,
        {
          params: { page, limit, search },
          headers: getAuthHeaders(),
        }
      );

      return {
        data: response.data.data || [],
        pagination: response.data.pagination || {
          page: 1,
          limit: 20,
          total: (response.data.data || []).length,
          totalPages: 1,
        },
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendCuratorInvites = createAsyncThunk(
  'curatorInvites/send',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/admin/curator-invites`,
        payload,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        data: response.data.data || {},
        message: response.data.message,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const resendCuratorInvite = createAsyncThunk(
  'curatorInvites/resend',
  async (inviteId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/admin/curator-invites/${inviteId}/resend`,
        {},
        {
          headers: getAuthHeaders(),
        }
      );

      return {
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCuratorInviteEmailPreview = async (inviteeEmail = '') => {
  const response = await axios.get(
    `${process.env.REACT_APP_BACKEND_URI}/admin/curator-invites/email-preview`,
    {
      params: inviteeEmail ? { inviteeEmail } : undefined,
      headers: getAuthHeaders(),
    }
  );

  return response.data?.data || null;
};

export const deleteCuratorInvite = createAsyncThunk(
  'curatorInvites/delete',
  async (inviteId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URI}/admin/curator-invites/${inviteId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return {
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const upsertInvite = (invites, nextInvite) => {
  const index = invites.findIndex(invite => invite.id === nextInvite.id);
  if (index === -1) {
    return [nextInvite, ...invites];
  }
  const updated = [...invites];
  updated[index] = nextInvite;
  return updated;
};

export const curatorInvitesSlice = createSlice({
  name: 'curatorInvitesSlice',
  initialState,
  reducers: {
    clearCuratorInvitesError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCuratorInvites.pending, state => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchCuratorInvites.fulfilled, (state, action) => {
        state.listLoading = false;
        state.invites = action.payload.data;
        state.invitesPagination = action.payload.pagination;
      })
      .addCase(fetchCuratorInvites.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(sendCuratorInvites.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(sendCuratorInvites.fulfilled, state => {
        state.actionLoading = false;
      })
      .addCase(sendCuratorInvites.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(resendCuratorInvite.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(resendCuratorInvite.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.invites = upsertInvite(state.invites, action.payload.data);
      })
      .addCase(resendCuratorInvite.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteCuratorInvite.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteCuratorInvite.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.invites = state.invites.filter(
          invite => invite.id !== action.payload.data.id
        );
      })
      .addCase(deleteCuratorInvite.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearCuratorInvitesError } = curatorInvitesSlice.actions;

export default curatorInvitesSlice.reducer;
